import React, { useContext, useEffect, useRef, useState } from 'react';

import {
  useMarketingActiveStatus,
  useMarketingCampaignContactability,
  useMarketingChannels,
  useMarketingContactability,
  useMarketingDestinations,
  useMarketingLeadStatus,
  useMarketingQueryProfit,
} from '../use-marketing-reports';

import { ActiveStatusTable } from './ActiveStatusTable';
import { CampaignContactabilityTable } from './CampaignContactabilityTable';
import { ContactabilityTables } from './ContactabilityTables';
import {
  DestinationContactability,
  DestinationLostReasons,
  DestinationProfitability,
  DestinationTimeline,
} from './DestinationsTable';
import { ExcelProfitSheet } from './ExcelProfitSheet';
import { ActiveRatioTable, LeadStatusTable } from './LeadStatusTable';
import { ForceExpandContext } from './month-table';
import { QueryChannelPanel } from './QueryChannelPanel';

import type { MarketingProfitResponse, MarketingSpendResponse } from '@utazzo/shared';

/* ------------------------------------------------------------------------ */
/*                          The page's section index                         */
/* ------------------------------------------------------------------------ */

interface OverviewSection {
  id: string;
  /** Named on the placeholder while the block has not loaded yet. */
  label: string;
  Block: (props: { months: string[] }) => React.JSX.Element;
}

/* ------------------------------------------------------------------------ */
/*                           Deferred section blocks                         */
/* ------------------------------------------------------------------------ */

/** The shimmer a block shows before it has scrolled into range. */
const BlockSkeleton = ({ label }: { label: string }): React.JSX.Element => (
  <div className="border-border bg-card min-h-70 rounded-xl border shadow-sm">
    <div className="border-border bg-muted/50 rounded-t-xl border-b px-5 py-3">
      <span className="text-muted-foreground text-sm font-semibold">{label}</span>
    </div>
    <div className="space-y-2 px-5 py-4">
      {[0, 1, 2, 3, 4, 5].map((row) => (
        <div key={row} className="bg-muted/60 h-7 animate-pulse rounded" />
      ))}
    </div>
  </div>
);

/**
 * A block of the report that does not fetch until it is nearly on screen.
 *
 * The Overview is every report on one page, and firing all the aggregates at
 * once would make the first screen wait on tables the operator has not
 * scrolled to yet. Each block mounts - and so fires its own read - once it
 * comes within a screen of the viewport, and the placeholder holds roughly its
 * height so the scrollbar does not jump as blocks fill in.
 *
 * An export overrides all of it. The file is read off the rendered tables, so a
 * block still waiting to be scrolled to would simply be absent from it - which
 * is what made an export taken from the top of the page contain one table.
 */
const Deferred = ({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}): React.JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  const exporting = useContext(ForceExpandContext);
  const shown = seen || exporting;

  useEffect(() => {
    const node = ref.current;
    if (seen || !node) return;
    // No IntersectionObserver (jsdom, very old browsers) - show it rather than
    // leave a section permanently empty.
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [seen]);

  return (
    <section id={id} ref={ref} className="scroll-mt-20 space-y-4">
      {shown ? children : <BlockSkeleton label={label} />}
    </section>
  );
};

/*
 * Each block owns its own read, so nothing is fetched until it mounts. Several
 * blocks share one read (the four destination tables, the two lead-status
 * ones); the query cache dedupes them, so the second block to mount costs
 * nothing.
 */

type MonthsProps = { months: string[] };

const ActiveRatioBlock = ({ months }: MonthsProps): React.JSX.Element => {
  // Funnel (Query -> Contacted -> Active) comes from the profit read so all three
  // figures reconcile; `months[0]` is the newest month in range.
  const profit = useMarketingQueryProfit(months);
  return <ActiveRatioTable month={profit.data?.months[0]} loading={profit.isLoading} />;
};

const RegionAgentBlock = ({ months }: MonthsProps): React.JSX.Element => {
  const contactability = useMarketingContactability(months);
  return (
    <div className="space-y-4">
      <ContactabilityTables data={contactability.data} loading={contactability.isLoading} />
    </div>
  );
};

const DestinationContactBlock = ({ months }: MonthsProps): React.JSX.Element => {
  const destinations = useMarketingDestinations(months);
  return <DestinationContactability data={destinations.data} loading={destinations.isLoading} />;
};

const ActiveStatusBlock = ({ months }: MonthsProps): React.JSX.Element => {
  const activeStatus = useMarketingActiveStatus(months);
  return <ActiveStatusTable data={activeStatus.data} loading={activeStatus.isLoading} />;
};

const TimelineBlock = ({ months }: MonthsProps): React.JSX.Element => {
  const destinations = useMarketingDestinations(months);
  return <DestinationTimeline data={destinations.data} loading={destinations.isLoading} />;
};

const CampaignBlock = ({ months }: MonthsProps): React.JSX.Element => {
  const campaigns = useMarketingCampaignContactability(months);
  return <CampaignContactabilityTable data={campaigns.data} loading={campaigns.isLoading} />;
};

const LostReasonBlock = ({ months }: MonthsProps): React.JSX.Element => {
  const destinations = useMarketingDestinations(months);
  return <DestinationLostReasons data={destinations.data} loading={destinations.isLoading} />;
};

const ProfitabilityBlock = ({ months }: MonthsProps): React.JSX.Element => {
  const destinations = useMarketingDestinations(months);
  return <DestinationProfitability data={destinations.data} loading={destinations.isLoading} />;
};

const ChannelBlock = ({ months }: MonthsProps): React.JSX.Element => {
  const channels = useMarketingChannels(months);
  return (
    <QueryChannelPanel channels={channels.data} loading={channels.isLoading} showKpis={false} />
  );
};

const LeadStatusBlock = ({ months }: MonthsProps): React.JSX.Element => {
  const leadStatus = useMarketingLeadStatus(months);
  return (
    <LeadStatusTable status={leadStatus.data} loading={leadStatus.isLoading} showSummary={false} />
  );
};

/**
 * Everything under the Query Profit and Spend sheet, in the order of the
 * client's workbook - the sheet they review the month from, block for block.
 * The full-screen workbook (and so the Excel file) runs in the same order; keep
 * the two in step. What the sheet does not carry comes last.
 */
const SECTIONS: OverviewSection[] = [
  { id: 'active-ratio', label: 'Query to Active Ratio', Block: ActiveRatioBlock },
  { id: 'region-agent', label: 'Region & Agent Contactibility', Block: RegionAgentBlock },
  {
    id: 'destination-contactability',
    label: 'Destination Wise Contactibility',
    Block: DestinationContactBlock,
  },
  { id: 'active-status', label: 'Month Wise Active Status', Block: ActiveStatusBlock },
  { id: 'booking-timeline', label: 'Destination Wise Booking Timeline', Block: TimelineBlock },
  { id: 'campaigns', label: 'Campaign wise Contactibility', Block: CampaignBlock },
  { id: 'lost-reasons', label: 'Destination wise Lost Region', Block: LostReasonBlock },
  {
    id: 'destination-profitability',
    label: 'Destination Wise Profitability',
    Block: ProfitabilityBlock,
  },
  { id: 'query-channel', label: 'Query Channel', Block: ChannelBlock },
  { id: 'lead-status', label: 'Lead Status', Block: LeadStatusBlock },
];

/* ------------------------------------------------------------------------ */
/*                                The Overview                               */
/* ------------------------------------------------------------------------ */

/**
 * The Overview tab: the WHOLE marketing report on one screen - headline tiles,
 * the Query Profit and Spend sheet, then every table that also has a tab of its
 * own, in the order of the client's workbook.
 *
 * The tabs are untouched. They are the focused view of one part, for reading it
 * without the rest in the way; this page is the workbook read end to end, which
 * is how the report is actually reviewed each month.
 */
export const MarketingOverview = ({
  months,
  profit,
  spend,
  loading,
  canEdit,
}: {
  months: string[];
  profit: MarketingProfitResponse | undefined;
  spend: MarketingSpendResponse | undefined;
  loading: boolean;
  canEdit: boolean;
}): React.JSX.Element => {
  return (
    <div className="space-y-4 pt-2">
      {/* KPI tiles removed (client request). The LPS-style lead intake breakdown
          now lives below the daily requirement in the page body, outside the tabs. */}

      {/* Always loaded: the sheet IS the report, and its read is already in
          flight from the page, which needs it for the header and cost gate. */}
      <section id="profit-spend" className="scroll-mt-20">
        <ExcelProfitSheet profit={profit} spend={spend} loading={loading} canEdit={canEdit} />
      </section>

      {SECTIONS.map(({ id, label, Block }) => (
        <Deferred key={id} id={id} label={label}>
          <Block months={months} />
        </Deferred>
      ))}
    </div>
  );
};
